// Utility functions for FarmDash

// Get current week in format YYYY-MM-Ww (e.g., "2026-02-W7")
export const getCurrentWeek = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  
  // Calculate week number of the month
  const firstDayOfMonth = new Date(year, now.getMonth(), 1);
  const dayOfMonth = now.getDate();
  const firstDayWeekday = firstDayOfMonth.getDay() || 7;
  const weekOfMonth = Math.ceil((dayOfMonth + firstDayWeekday - 1) / 7);
  
  return `${year}-${month}-W${weekOfMonth}`;
};

// Get week number from week string
export const getWeekNumber = (weekString) => {
  if (!weekString) return 0;
  const match = weekString.match(/W(\d+)/);
  return match ? parseInt(match[1]) : 0;
};

// Get week label for display
export const getWeekLabel = (week) => {
  if (typeof week === 'string' && week.includes('W')) {
    return week;
  }
  return `W${week}`;
};

// Check if a week string is the current week
export const isCurrentWeek = (weekString) => {
  return weekString === getCurrentWeek();
};

export const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return Number(num).toLocaleString('en-US', { maximumFractionDigits: 2 });
};

export const parseTGEDate = (tgeString) => {
  if (!tgeString || tgeString === 'TBD') return '';
  
  const year = tgeString.match(/\d{4}/)?.[0];
  if (!year) return '';
  
  if (tgeString.includes('Q1')) return `${year}-03-31`;
  if (tgeString.includes('Q2')) return `${year}-06-30`;
  if (tgeString.includes('Q3')) return `${year}-09-30`;
  if (tgeString.includes('Q4')) return `${year}-12-31`;
  if (tgeString.includes('H1')) return `${year}-06-30`;
  if (tgeString.includes('H2')) return `${year}-12-31`;
  
  return tgeString.includes('-') ? tgeString : `${year}-12-31`;
};

export const calculateWeeksInRange = (timeRange) => {
  const currentWeek = getCurrentWeek();
  
  if (timeRange === 'week') {
    return [currentWeek];
  }
  
  // For month, quarter, year - return null to get all weeks
  return null;
};

export const getProjectStats = (weeks, weeklyData, projectKey) => {
  const stats = { volume: 0, cost: 0, points: 0, fees: 0, customMetric: 0 };
  
  Object.entries(weeklyData.weeks || {}).forEach(([week, weekEntries]) => {
    if (weeks && !weeks.includes(week)) {
      return;
    }
    
    weekEntries.forEach(entry => {
      if (entry.project.toLowerCase().includes(projectKey.toLowerCase())) {
        stats.volume += entry.volume || 0;
        stats.cost += entry.cost || 0;
        stats.points += entry.points || 0;
        stats.fees += entry.fees || 0;
        stats.customMetric += entry.customMetric || 0;
      }
    });
  });
  
  return stats;
};

export const getTotalPoints = (weeklyData, projectKey) => {
  let total = 0;
  Object.values(weeklyData.weeks || {}).forEach(weekEntries => {
    weekEntries.forEach(entry => {
      if (entry.project.toLowerCase().includes(projectKey.toLowerCase())) {
        total += entry.points || 0;
      }
    });
  });
  return total;
};

export const getTotalFees = (weeklyData, projectKey) => {
  let total = 0;
  Object.values(weeklyData.weeks || {}).forEach(weekEntries => {
    weekEntries.forEach(entry => {
      if (entry.project.toLowerCase().includes(projectKey.toLowerCase())) {
        total += entry.fees || 0;
      }
    });
  });
  return total;
};

export const getTotalCustomMetric = (weeklyData, projectKey) => {
  let total = 0;
  Object.values(weeklyData.weeks || {}).forEach(weekEntries => {
    weekEntries.forEach(entry => {
      if (entry.project.toLowerCase().includes(projectKey.toLowerCase())) {
        total += entry.customMetric || 0;
      }
    });
  });
  return total;
};

export const getTotalCosts = (weeklyData, projectKey) => {
  let total = 0;
  Object.values(weeklyData.weeks || {}).forEach(weekEntries => {
    weekEntries.forEach(entry => {
      if (entry.project.toLowerCase().includes(projectKey.toLowerCase())) {
        total += entry.cost || 0;
      }
    });
  });
  return total;
};

export const getProjectHistory = (weeklyData, projectKey) => {
  const history = [];
  
  Object.keys(weeklyData.weeks || {})
    .sort((a, b) => b.localeCompare(a))
    .forEach(week => {
      let weekPoints = 0;
      let weekVolume = 0;
      let weekCost = 0;
      let weekFees = 0;
      let weekCustomMetric = 0;
      
      weeklyData.weeks[week].forEach(entry => {
        if (entry.project.toLowerCase().includes(projectKey.toLowerCase())) {
          weekPoints += entry.points || 0;
          weekVolume += entry.volume || 0;
          weekCost += entry.cost || 0;
          weekFees += entry.fees || 0;
          weekCustomMetric += entry.customMetric || 0;
        }
      });
      
      if (weekPoints > 0 || weekVolume > 0 || weekFees > 0 || weekCustomMetric > 0) {
        history.push({
          week,
          points: weekPoints,
          volume: weekVolume,
          cost: weekCost,
          fees: weekFees,
          customMetric: weekCustomMetric
        });
      }
    });
  
  return history;
};

// Parse number that may contain dots as thousand separators
export const parseFormattedNumber = (value) => {
  if (!value || value === '') return 0;
  const cleanValue = String(value).replace(/\./g, '').replace(/,/g, '.');
  const parsed = parseFloat(cleanValue);
  return isNaN(parsed) ? 0 : parsed;
};

// Format number with dots as thousand separators for display in inputs
export const formatNumberInput = (value) => {
  if (!value && value !== 0) return '';
  const num = typeof value === 'string' ? parseFormattedNumber(value) : value;
  if (isNaN(num)) return '';
  return Math.floor(num).toLocaleString('es-ES');
};